import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Browser } from '@capacitor/browser';
import axiosInstance from './axiosCreator';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import {
  isExternalNotificationLink,
  isRelativeNotificationLink,
} from '../lib/notificationLinks';

export type PushTapTarget =
  { kind: 'internal'; link: string } | { kind: 'external'; link: string };

/**
 * Where a tapped push should take the user. Bell pushes carry the
 * notification's `link` in their payload; anything else (no link, a malformed
 * one, a reminder push) just opens the app.
 */
export const resolvePushTapTarget = (data: unknown): PushTapTarget | null => {
  if (!data || typeof data !== 'object') return null;
  const link = (data as { link?: unknown }).link;
  if (typeof link !== 'string' || link.length === 0 || link.length > 500) {
    return null;
  }
  if (isRelativeNotificationLink(link)) return { kind: 'internal', link };
  if (isExternalNotificationLink(link)) return { kind: 'external', link };
  return null;
};

// initializePushNotifications runs again whenever the signed-in user's
// session data changes, but the plugin listeners must exist exactly once or a
// single tap would navigate (or open the browser) several times. The current
// user and navigator live here so the one-time listeners never go stale.
let listenersAttached = false;

let current: {
  userId: string;
  deviceId: string | null;
  navigate: (link: string) => void;
} | null = null;

// Bumped on every initialize and on logout. An initialize that was mid-await
// when the session changed must not write `current` back or register a token
// for the account that is no longer signed in.
let generation = 0;
// The (user, device, token) last sent to register-device. Every register()
// call makes the plugin emit `registration` again with the same token, and
// those repeats, arriving together, were what raced the backend into storing
// one device several times. One registration per session is enough.
let lastRegistration: { key: string } | null = null;

// The registration the backend accepted, which sign-out detaches again.
let registeredDevice: {
  userId: string;
  deviceId: string;
  token: string;
} | null = null;

/** How long Log Out waits for the device to be detached (ORA-1089). */
export const PUSH_UNREGISTER_TIMEOUT_MS = 2_000;

/** Call on logout so a late `registration` event registers nothing. */
export const clearPushNotificationSession = () => {
  generation += 1;
  current = null;
  lastRegistration = null;
  registeredDevice = null;
};

/**
 * Before Log Out revokes the session: detaches this device's push token from
 * the account, so the next person on the device does not get the last one's
 * notifications (ORA-1089). Best effort: never throws and never holds the
 * sign-out longer than `PUSH_UNREGISTER_TIMEOUT_MS`. Then forgets the session.
 */
export const unregisterPushDeviceForSignOut = async (): Promise<void> => {
  const device = registeredDevice;
  clearPushNotificationSession();
  if (!device || !Capacitor.isNativePlatform()) return;

  const request = new AbortController();
  let timer: number | undefined;
  try {
    await Promise.race([
      axiosInstance.post(
        `/apn/${device.userId}/unregister-device`,
        { token: device.token, deviceId: device.deviceId },
        { signal: request.signal }
      ),
      new Promise<never>((_, reject) => {
        timer = window.setTimeout(() => {
          request.abort();
          reject(new Error('Push unregister timed out'));
        }, PUSH_UNREGISTER_TIMEOUT_MS);
      }),
    ]);
  } catch (error) {
    console.warn('Could not unregister the push device:', error);
  } finally {
    window.clearTimeout(timer);
  }
};

const attachListeners = () => {
  if (listenersAttached) return;
  listenersAttached = true;

  // Called when the user taps a notification. Attached before any permission
  // or registration step so a launch-from-tap is handled even when those fail.
  void PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action) => {
      const target = resolvePushTapTarget(action.notification.data);
      if (!target || !current) return;
      if (target.kind === 'external') {
        void Browser.open({ url: target.link });
        return;
      }
      current.navigate(target.link);
    }
  );

  // Called when registration successfully returns a device token
  void PushNotifications.addListener('registration', (token: Token) => {
    if (!current?.deviceId) return;
    const key = `${current.userId}:${current.deviceId}:${token.value}`;
    if (lastRegistration?.key === key) return;

    const attempt = { key };
    lastRegistration = attempt;

    const device = {
      userId: current.userId,
      deviceId: current.deviceId,
      token: token.value,
    };

    void sendTokenToBackend(device.token, device.deviceId, device.userId).then(
      (sent) => {
        // Let the next registration event retry a failed send, unless a newer
        // session has already registered the same tuple in the meantime.
        if (!sent && lastRegistration === attempt) lastRegistration = null;
        // Remembered for sign-out, unless the session ended meanwhile.
        if (sent && lastRegistration === attempt) registeredDevice = device;
      }
    );
  });

  // Called when a push notification is received
  void PushNotifications.addListener(
    'pushNotificationReceived',
    (notification) => {
      console.log('Push notification received:', notification);
      // Handle foreground notification presentation here if needed.
    }
  );
};

export async function initializePushNotifications(
  userId: string,
  navigate: (link: string) => void
) {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return false;
  }

  const myGeneration = ++generation;
  const isCurrent = () => generation === myGeneration;
  current = { userId, deviceId: current?.deviceId ?? null, navigate };
  attachListeners();

  try {
    const deviceId = await Device.getId();
    if (!isCurrent()) return false;

    if (!deviceId.identifier) {
      return false;
    }
    current = { userId, deviceId: deviceId.identifier, navigate };

    let permStatus = await PushNotifications.checkPermissions();
    if (!isCurrent()) return false;
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
      if (!isCurrent()) return false;
    }
    if (permStatus.receive !== 'granted') {
      return false;
    }

    await PushNotifications.register();

    return true;
  } catch (error) {
    console.warn('Push notifications unavailable:', error);
    return false;
  }
}

async function sendTokenToBackend(
  token: string,
  deviceId: string,
  userId: string
): Promise<boolean> {
  try {
    await axiosInstance.post(`/apn/${userId}/register-device`, {
      token,
      deviceId,
    });

    return true;
  } catch (error) {
    console.error('Error sending token to backend:', error);
    return false;
  }
}
